import { Orcamento, OutboxItem, SyncResult, PerfilMei } from '@/contracts/schemas';
import { storage } from './storage';

/**
 * Algoritmo puro e determinístico de reconciliação entre orçamentos da nuvem e locais:
 * - Regra 1: Entidades com mutação pendente no Outbox (UPSERT_ORCAMENTO) NÃO são sobrescritas pela nuvem (Local Unsynced Wins).
 * - Regra 2: Entidades com exclusão pendente no Outbox (DELETE_ORCAMENTO) NÃO são ressuscitadas pela nuvem.
 * - Regra 3: Entidades existentes na nuvem que não existem localmente são adicionadas.
 * - Regra 4: Entidades existentes em ambos sem pendência local são reconciliadas pela versão mais recente.
 */
export function mergeCloudAndLocalOrcamentos(
  cloudList: Orcamento[],
  localList: Orcamento[],
  outbox: OutboxItem[]
): Orcamento[] {
  const pendingUpsertUuids = new Set(
    outbox.filter(item => !item.synced && item.action === 'UPSERT_ORCAMENTO').map(item => item.entityId)
  );

  const pendingDeleteUuids = new Set(
    outbox.filter(item => !item.synced && item.action === 'DELETE_ORCAMENTO').map(item => item.entityId)
  );

  // Mapa base partindo do local (respeitando mutações pendentes)
  const mergedMap = new Map<string, Orcamento>();

  for (const local of localList) {
    if (pendingDeleteUuids.has(local.uuid) || (local.id && pendingDeleteUuids.has(local.id))) {
      // Deletado localmente, não inclui
      continue;
    }
    mergedMap.set(local.uuid, local);
  }

  // Processa itens vindos da nuvem
  for (const cloud of cloudList) {
    if (pendingDeleteUuids.has(cloud.uuid) || (cloud.id && pendingDeleteUuids.has(cloud.id))) {
      // Foi deletado localmente, ignora o da nuvem para não ressuscitar
      continue;
    }

    if (pendingUpsertUuids.has(cloud.uuid) || (cloud.id && pendingUpsertUuids.has(cloud.id))) {
      // Local possui mutações pendentes no Outbox: mantém local (Local Unsynced Wins)
      continue;
    }

    const localExisting = mergedMap.get(cloud.uuid);
    if (!localExisting) {
      // Item existe na nuvem mas não localmente: adiciona
      mergedMap.set(cloud.uuid, cloud);
    } else {
      // Ambos existem e o local não tem mutação pendente: compara timestamps
      const cloudTime = new Date(cloud.dataCriacao || 0).getTime();
      const localTime = new Date(localExisting.dataCriacao || 0).getTime();
      if (cloudTime >= localTime) {
        mergedMap.set(cloud.uuid, cloud);
      }
    }
  }

  // Converte de volta para array ordenado pela dataCriacao decrescente
  return Array.from(mergedMap.values()).sort((a, b) => {
    const timeA = new Date(a.dataCriacao || 0).getTime();
    const timeB = new Date(b.dataCriacao || 0).getTime();
    return timeB - timeA;
  });
}

/**
 * Drena a fila local. Não há nuvem: nada é enviado para fora do aparelho.
 */
export async function syncCloudData(_userIdParam?: string): Promise<SyncResult> {
  const drainResult = await storage.drainOutbox();
  return {
    synced: true,
    pulledCount: 0,
    pushedCount: drainResult.succeeded
  };
}

/**
 * O perfil da empresa fica neste aparelho.
 */
export async function syncEmpresaData(_userIdParam?: string): Promise<{ success: boolean; perfil: PerfilMei; error?: string }> {
  return { success: true, perfil: storage.getPerfilMei() };
}

